import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Phone, Lock, Eye, EyeOff, Shield, Building2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Alert } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';

// ── Validation Schema ──────────────────────
const loginFormSchema = z.object({
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^(\+880|880|0)?1[3-9]\d{8}$/, 'Enter a valid BD phone number'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginFormSchema>;

// ============================================
// LOGIN PAGE
// ============================================
export default function LoginPage() {
  const { login, loading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { phone: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    try {
      await login(data.phone, data.password);
    } catch {
      // Error handled by hook
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-bold text-slate-900">Welcome Back</h2>
        <p className="mt-1 text-sm text-slate-500">
          Sign in to manage your fuel station
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Phone Number */}
        <FormField
          label="Phone Number"
          htmlFor="phone"
          required
          error={errors.phone?.message}
        >
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="phone"
              type="tel"
              placeholder="01XXXXXXXXX"
              autoComplete="tel"
              error={!!errors.phone}
              className="pl-10"
              {...register('phone')}
            />
          </div>
        </FormField>

        {/* Password */}
        <FormField
          label="Password"
          htmlFor="password"
          required
          error={errors.password?.message}
        >
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter password"
              autoComplete="current-password"
              error={!!errors.password}
              className="pl-10 pr-10"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </FormField>

        {/* Remember Me + Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300" />
            Remember me
          </label>
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-blue-700 hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        {/* Submit */}
        <Button type="submit" className="w-full" size="lg" loading={loading}>
          Sign In
        </Button>
      </form>

      {/* Divider */}
      <Separator text="OR" className="my-5" />

      {/* OTP Login */}
      <Link to="/otp-login">
        <Button variant="outline" className="w-full" size="lg">
          <Phone className="h-4 w-4" />
          Sign in with OTP
        </Button>
      </Link>

      {/* Role Badges */}
      <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 p-3">
        <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Multi-Tenant Access
        </p>
        <div className="flex flex-wrap justify-center gap-1.5">
          {[
            { icon: Shield, label: 'Super Admin', color: 'bg-red-100 text-red-700' },
            { icon: Building2, label: 'Station Owner', color: 'bg-blue-100 text-blue-700' },
            { icon: Users, label: 'Manager', color: 'bg-green-100 text-green-700' },
          ].map((role) => (
            <span
              key={role.label}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${role.color}`}
            >
              <role.icon className="h-3 w-3" />
              {role.label}
            </span>
          ))}
        </div>
      </div>

      {/* Register Link */}
      <p className="mt-5 text-center text-sm text-slate-500">
        Don't have an account?{' '}
        <Link to="/register" className="font-semibold text-blue-700 hover:underline">
          Register your station
        </Link>
      </p>
    </div>
  );
}
